import {
  Activity,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Eye,
  LogIn,
  Megaphone,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DASHBOARD_ROUTES } from "../../constants/constant";
import {
  useGetCampaignsAnalyticsApiCallQuery,
  useGetCampaignsApiCallQuery,
} from "../../services/api/campaigns/campaigns.api";
import { useGetUsersGraphAnalyticsApiCallQuery } from "../../services/api/users/users.api";
import { getTimeRemaining } from "../../utils/getTimeRemaining";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Progress } from "../ui/progress";
import { useGetNotificationActivityApiCallQuery } from "../../services/api/auth/auth.api";
import { timeAgo } from "../../utils/timeAgo";

// Mock data
const mockKPIData = {
  totalParticipants: 15432,
  activeCampaigns: 8,
  totalTasksCompleted: 47892,
  totalValueDistributed: 2890000,
};

const mockChartData = [
  { date: "2024-01-01", users: 1200 },
  { date: "2024-01-02", users: 1350 },
  { date: "2024-01-03", users: 1500 },
  { date: "2024-01-04", users: 1890 },
  { date: "2024-01-05", users: 2100 },
  { date: "2024-01-06", users: 2400 },
  { date: "2024-01-07", users: 2800 },
];

const mockActiveCampaigns = [
  {
    id: "1",
    name: "Project X Launch",
    progress: 75,
    participants: 3245,
    tasksCompleted: 12890,
    timeRemaining: "5 days",
    status: "active" as const,
  },
  {
    id: "2",
    name: "Community Growth",
    progress: 45,
    participants: 1832,
    tasksCompleted: 8432,
    timeRemaining: "12 days",
    status: "active" as const,
  },
  {
    id: "3",
    name: "Token Migration",
    progress: 90,
    participants: 5621,
    tasksCompleted: 18743,
    timeRemaining: "2 days",
    status: "active" as const,
  },
];

const mockRecentActivity = [
  {
    id: "1",
    type: "user_flagged",
    message: "User 0x1234...5678 was flagged for suspicious activity",
    timestamp: "2 minutes ago",
    severity: "warning" as const,
  },
  {
    id: "2",
    type: "campaign_ended",
    message: 'Campaign "Early Adopter" has just ended',
    timestamp: "15 minutes ago",
    severity: "info" as const,
  },
  {
    id: "3",
    type: "mass_signup",
    message: "127 new participants joined in the last hour",
    timestamp: "1 hour ago",
    severity: "success" as const,
  },
  {
    id: "4",
    type: "system_alert",
    message: "Token distribution wallet balance is running low",
    timestamp: "2 hours ago",
    severity: "error" as const,
  },
];

const MainDashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState("7d");

  const { data: analyticsCampaigns, refetch: refetchCampaigns } =
    useGetCampaignsAnalyticsApiCallQuery();
  const { data: campaignsData } = useGetCampaignsApiCallQuery({
    status: "Active",
  });

  const { data: analyticsGraph } = useGetUsersGraphAnalyticsApiCallQuery();
  const { data: notificationActivity } = useGetNotificationActivityApiCallQuery(
    {
      page: 1,
      limit: 6,
    }
  );

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "user_flagged":
        return <AlertTriangle className="h-4 w-4" />;
      case "campaign_ended":
        return <Megaphone className="h-4 w-4" />;
      case "mass_signup":
        return <Users className="h-4 w-4" />;
      case "system_alert":
        return <AlertTriangle className="h-4 w-4" />;

      case "Login":
        return <Users className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "error":
        return "text-red-600 bg-red-50";
      case "warning":
        return "text-yellow-600 bg-yellow-50";
      case "success":
        return "text-green-600 bg-green-50";
      default:
        return "text-blue-600 bg-blue-50";
    }
  };

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Analytics Overview
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor your airdrop platform performance
          </p>
        </div>

        {/* <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 Hours</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
        </div> */}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Participants
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsCampaigns?.result?.totalParticipants || 0}
            </div>
            {/* <p className="text-xs text-muted-foreground">
              <span className="text-green-600 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                +12.5%
              </span>
              from last period
            </p> */}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Campaigns
            </CardTitle>
            <Megaphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {/* {mockKPIData.activeCampaigns} */}
              {analyticsCampaigns?.result?.totalActiveCampaigns || 0}
            </div>
            {/* <p className="text-xs text-muted-foreground">
              <span className="text-blue-600">3 ending this week</span>
            </p> */}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Tasks Completed
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {/* {mockKPIData.totalTasksCompleted.toLocaleString()} */}
              {analyticsCampaigns?.result?.totalTasksCompleted || 0}
            </div>
            {/* <p className="text-xs text-muted-foreground">
              <span className="text-green-600 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                +8.2%
              </span>
              completion rate
            </p> */}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Value Distributed
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {/* ${mockKPIData.totalValueDistributed.toLocaleString()} */}
              {analyticsCampaigns?.result?.totalDistributed || 0}
            </div>
            {/* <p className="text-xs text-muted-foreground">
              Across all campaigns
            </p> */}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Sign-up Trend Chart */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>User Sign-up Trend</CardTitle>
            <CardDescription>
              New participant registrations over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={analyticsGraph?.result?.map((item) => ({
                    name: `${item.monthName} ${item.year}`, // X-axis label
                    users: item.totalUser, // Y-axis value
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  {/* X Axis with month-year */}
                  <XAxis dataKey="name" />

                  <YAxis />

                  <Tooltip
                    formatter={(value: number) => [`${value} Users`, "Users"]}
                    labelFormatter={(label) => label}
                  />

                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: "#3b82f6" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity Feed */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Latest platform events and notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {notificationActivity?.result &&
              notificationActivity?.result?.docs?.length > 0 ? (
                notificationActivity?.result?.docs.map((activity) => (
                  <div
                    key={activity._id}
                    className="flex items-start space-x-3"
                  >
                    <div className={`p-2 rounded-full`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">
                        {activity?.activity}
                      </p>
                      <p className="text-xs text-gray-500">
                        {timeAgo(activity?.createdAt)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div>
                  <p className="text-sm text-gray-900 text-center">
                    No recent activity
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Campaign Snapshot */}
      <Card>
        <CardHeader>
          <CardTitle>Active Campaign Snapshot</CardTitle>
          <CardDescription>
            Overview of currently running airdrop campaigns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {campaignsData?.result &&
              campaignsData?.result?.docs?.length > 0 &&
              campaignsData?.result?.docs.map((campaign) => {
                const { days, hours, minutes, status } = getTimeRemaining(
                  campaign?.startDate,
                  campaign?.endDate
                );
                return (
                  <div
                    key={campaign._id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-medium text-gray-900">
                          {campaign.name}
                        </h3>
                        <Badge variant="default">{campaign?.status}</Badge>
                      </div>

                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div>
                          <p className="text-sm text-gray-500">Participants</p>
                          <p className="font-medium">{campaign.totalJoined}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">
                            Tasks Completed
                          </p>
                          <p className="font-medium">
                            {campaign.totalCompleteTask}
                            {/* {campaign?.to} */}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">
                            Time Remaining
                          </p>
                          <p className="font-medium">
                            {status === "ended"
                              ? "Ended"
                              : `${days}d
                            ${hours}h ${minutes}m`}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span>{campaign.progress}%</span>
                        </div>
                        <Progress value={campaign.progress} className="h-2" />
                      </div>
                    </div>

                    <div className="ml-6">
                      <Link
                        to={DASHBOARD_ROUTES?.CAMPAIGNS_DETAIL(campaign._id)}
                      >
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-2" />
                          View Dashboard
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MainDashboard;
